import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/TagsService';
import { useAppSelector } from '../../../hooks/useTypedReactRedux';
import { selectionSelector } from '../selectors';

export const useTags = () => {
    const queryClient = useQueryClient();
    const selection = useAppSelector((state) => state.tags ? selectionSelector(state) : {});
    
    const tagsQuery = useInfiniteQuery({
        queryKey: ['tags'],
        queryFn: ({ pageParam }: { pageParam: string | null }) => 
            fetchRecords({ limit: 10, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: { id: string, name: string }[]) => {
            const promises = ids.map(item => deleteRecord(item.id, item.name));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tags'] });
        }
    });

    const fetchRecordsAction = () => {
        if (tagsQuery.hasNextPage) {
            tagsQuery.fetchNextPage();
        }
    };

    const refetchRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['tags'] });
    };

    const deleteRecords = () => {
        const selectedIds = Object.keys(selection).map(key => ({
            id: selection[key].id,
            name: selection[key].name
        }));
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const allItems = tagsQuery.data?.pages?.flatMap(page => 
        (page && 'items' in page) ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        visibleCount: allItems.length,
        loading: tagsQuery.isLoading,
        nextToken: tagsQuery.hasNextPage ? 'has-more' : null,
        isFetchingNextPage: tagsQuery.isFetchingNextPage,
        fetchRecords: fetchRecordsAction,
        refetchRecords,
        deleteRecords,
    };
};
